import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { ProductSwatches } from "@app/scripts/react-components/ProductSwatches";
import { addToCart, CustomProductData, getOptionIndex, getOptionKey } from "@app/scripts/utils";
import { ElasticProduct, ElasticVariant } from "@usereactify/search";
import { ProductCard } from "./ProductCard";
import { Swatch } from "@app/scripts/types";

// selectedVariantImage: string

type Props = {
  initialVariant?: ElasticVariant;
  product: ElasticProduct;
  quickAddState: boolean;
  onSale: ProductCard["onSale"];
  formattedPrice: ProductCard["formattedPrice"];
  formattedCompareAtPrice: ProductCard["formattedCompareAtPrice"];
  variantImages: CustomProductData["variant_images"];
  closeQuickAdd: VoidFunction;
  openPersonalisationDrawer: VoidFunction;
  swatches: Swatch[];
  colourOptions: string[]
  setTopLevelVariant: React.Dispatch<React.SetStateAction<ElasticVariant | undefined>>
  updateVariantVariables: (matchedVariant: ElasticVariant) => void; 
};

export const QuickAdd: React.FC<Props> = ({
  initialVariant,
  product,
  variantImages,
  quickAddState,
  onSale,
  formattedPrice,
  formattedCompareAtPrice,
  closeQuickAdd,
  openPersonalisationDrawer,
  swatches,
  colourOptions,
  setTopLevelVariant,
  updateVariantVariables,
}) => {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedVariant, setSelectedVariant] = useState(initialVariant);

  const [isLoading, setIsLoading] = useState(false);

  const quickAddDrawerRef = useRef(null);
  const quickAddOverlayRef = useRef(null);

  const colourKey = getOptionKey("Colour", product.options);

  const options = useMemo(() => {
    const indexes = product.options?.map((option) => getOptionIndex(option, product.options));

    return indexes
      .map((index) => {
        if (index == -1) return null;

        const key = `option${index + 1}`;
        const values = product.variants?.map((variant) => variant?.[key])?.filter(Boolean);

        return {
          name: product.options[index],
          key: `option${index + 1}`,
          values: [...new Set(values)],
        };
      })
      ?.filter(Boolean);

    // return keys
  }, [product.options]);

  useEffect(() => {
    if (quickAddState) {
      quickAddDrawerRef.current?.classList.add("active");
      quickAddOverlayRef.current?.classList.add("active");
    }
  }, [quickAddState]);

  const closeQuickAddDrawer = () => {
    const quickAddTarget = quickAddDrawerRef.current as any;
    const overlayTarget = quickAddOverlayRef.current as any;

    quickAddTarget.classList.remove("active");
    overlayTarget.classList.remove("active");

    setTimeout(() => {
      closeQuickAdd();
    }, 300);
  };

  const handlePersOpen = useCallback(() => {
    setTopLevelVariant(selectedVariant);
    openPersonalisationDrawer();
    closeQuickAdd();
  }, [selectedVariant, openPersonalisationDrawer, closeQuickAdd, setTopLevelVariant]);

  // initial options
  useEffect(() => {
    const firstOptions = options.reduce((prev, curr) => {
      return curr?.key
        ? {
            ...prev,
            [curr.key]: initialVariant?.[curr.key],
          }
        : prev;
    }, {});
    setSelectedOptions(firstOptions);
  }, [initialVariant]);

  const selectOption = useCallback(
    (key: string, value: string) => {
      const selected = {
        ...selectedOptions,
        [key]: value,
      };
      const optValues = Object.values(selected);
      const optKeys = Object.keys(selected);

      const matchingVariant = product?.variants?.find((variant) => {
        return optKeys.map((key) => optValues.includes(variant?.[key]))?.filter(Boolean)?.length == optKeys?.length;
      });
      if (matchingVariant) {
        updateVariantVariables(matchingVariant);
        setSelectedOptions(selected);
        setSelectedVariant(matchingVariant);
      }
    },
    [selectedOptions, product?.variants]
  );

  const images = useMemo(() => {
    let variantImage = undefined;

    if (selectedVariant?.id && variantImages?.[selectedVariant.id]) {
      const match = variantImages?.[selectedVariant.id];
      variantImage = `${match}${match.includes("?") ? "&" : "?"}width=340`;
    }

    return variantImage ? [{ src: variantImage }, ...(product.images?.slice(1) || [])] : product.images;
  }, [product.images, selectedVariant, variantImages]);

  const addToBag = async () => {
    setIsLoading(true);
    const variantId = selectedVariant?.id;

    try {
      if (!variantId) throw "No variant id found";

      await addToCart({
        id: variantId,
        qty: 1,
      });
      closeQuickAddDrawer();
    } catch (e) {
      console.error(e);
    }

    setIsLoading(false);
  };

  const handleColorClick = useCallback((colour: string) => {
    if (!colourKey) return;
    selectOption(colourKey, colour);
  }, [selectedOptions, colourKey])

  return (
    <>
      <div className="quick-add__overlay" ref={quickAddOverlayRef} onClick={closeQuickAddDrawer}></div>
      <div className="quick-add" ref={quickAddDrawerRef}>
        <div className="quick-add__heading">
          <span>Quick add</span>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            onClick={closeQuickAddDrawer}
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12.5848 0L14 1.41517L8.414 7L14 12.5848L12.5848 14L7 8.414L1.41517 14L0 12.5848L5.586 7L0 1.41517L1.41517 0L7 5.586L12.5848 0Z"
              fill="black"
            />
          </svg>
        </div>

        <div className="quick-add__slide">
          <Splide
            options={{
              gap: "4px",
              arrows: false,
              drag: true,
              width: "100%",
              autoHeight: true,
              perPage: 1,
              pagination: false,
              direction: "ltr",
              breakpoints: {
                767: {
                  width: `auto`,
                  height: "250px",
                },
              },
            }}
          >
            {images?.map((image: any) => {
              return (
                <SplideSlide>
                  <img src={image.src} alt={product.title} />
                </SplideSlide>
              );
            })}
          </Splide>
        </div>

        <div className="quick-add__info">
          <span className="quick-add__vendor">{product.vendor}</span>

          <span className="quick-add__title">{product.title}</span>

          <div className="quick-add__prices">
            <span className={`quick-add__price ${onSale ? "text-brand-sale" : null}`}>{formattedPrice}</span>

            {onSale && (
              <span
                className="quick-add__price-sale"
                style={{
                  textDecoration: "line-through",
                }}
              >
                {formattedCompareAtPrice}
              </span>
            )}
          </div>
        </div>

        <div className="quick-add__variants">
          {product.tags.includes("Pen Personalisation") || product.tags.includes("Paper Personalisation") ? (
            <div className="personalise_me_quick_add" style={{ flexDirection: "row" }}>
              <span className="personalisation_price_label">Personalise from $19.95</span>
              <button onClick={handlePersOpen} className="personalise_me" style={{ fontWeight: 600 }}>
                PERSONALISE ME
              </button>
            </div>
          ) : null}

          {colourOptions?.length ? (
            <div className="quick-add__colours">
              <div className="quick-add__variant-heading">
                Select Colour: <span>{selectedOptions?.[colourKey]}</span>
              </div>
              <div className="quick-add__variant-values">
                <ProductSwatches
                  selectedColour={selectedOptions?.[colourKey]}
                  colourOptions={colourOptions}
                  swatches={swatches}
                  handleColorClick={handleColorClick}
                  swatchStyle={{
                    borderRadius: "50%",
                    height: "32px",
                    width: "32px",
                    padding: "2px",
                  }}
                />
              </div>
            </div>
          ) : null}

          {options
            ?.filter((opt) => opt?.name !== "Colour")
            ?.map((option) => (
              <div key={`${option?.key}`} className="quick-add__options">
                <div className="quick-add__variant-heading">
                  Select {option?.name}: <span>{selectedOptions?.[option?.key]}</span>
                </div>
                <div className="quick-add__variant-values">
                  {option?.values?.map((value: any) => {
                    return (
                      <span
                        key={`${option.key}${value}`}
                        className="option"
                        onClick={() => selectOption(option.key, value)}
                        data-value={value}
                        data-key={option.key}
                        style={selectedOptions?.[option?.key] === value ? { borderColor: "black" } : undefined}
                      >
                        {value}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>

        <div className="quick-add__cta">
          {!selectedVariant?.available ? (
            <>
              <span
                className="add-to-cart"
                data-swym-custom-watchlist
                data-url={`https://${Shopify.shop}/products/${product.handle}`}
                data-variant-id={selectedVariant?.id}
                data-product-id={product.id}
              >
                NOTIFY ME
              </span>
              <swym-notify></swym-notify>
            </>
          ) : (
            <span
              onClick={isLoading ? undefined : addToBag}
              className="add-to-cart"
              style={{ opacity: isLoading ? 0.6 : 1, pointerEvents: isLoading ? "none" : "auto" }}
            >
              {isLoading ? "Loading..." : "ADD TO CART"}
            </span>
          )}

          <a href={product.url}>view product details</a>
        </div>
      </div>
    </>
  );
};

const ProductOption = () => {};
